import type { ReactNode } from 'react';

/**
 * The shared honest-failure surface, following coding-standards §3.2's "error, nothing on screen"
 * row: it names what failed, states that the user's data is safe, and offers the next action.
 *
 * The data-safety line is not reassurance padding — a screen that empties without explanation is
 * read as data loss, and a rep who believes their pipeline vanished will act on that belief.
 */
export function FailureSurface({
    what,
    detail,
    action,
}: {
    readonly what: string;
    readonly detail?: string | undefined;
    readonly action: ReactNode;
}) {
    return (
        <div
            role="alert"
            style={{
                margin: 'var(--et-space-8) auto',
                maxWidth: 'var(--et-layout-message-max)',
                // A card: §5's Card/Panel row — surface/card, border/card, radius/lg.
                padding: 'var(--et-space-6)',
                background: 'var(--et-surface-card)',
                border: '1px solid var(--et-border-card)',
                borderRadius: 'var(--et-radius-lg)',
            }}
        >
            <h1
                style={{
                    margin: 0,
                    fontFamily: 'var(--et-type-title-face)',
                    fontSize: 'var(--et-type-title-size)',
                    fontWeight: 'var(--et-type-title-weight)',
                    lineHeight: 'var(--et-type-title-line)',
                    letterSpacing: 'var(--et-type-title-tracking)',
                    color: 'var(--et-text-heading)',
                }}
            >{`Couldn't load ${what}.`}</h1>
            {/* text/secondary rather than text/muted throughout this file: this is body-size copy
                the user must read, which §2 forbids muted (4.1:1) from carrying. */}
            <p style={{ color: 'var(--et-text-secondary)' }}>
                Your data is safe — nothing was lost. This is a problem with the app, not with your account.
            </p>
            {detail ? (
                <p
                    style={{
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: 'var(--et-type-secondary-size)',
                        color: 'var(--et-text-secondary)',
                        overflowWrap: 'anywhere',
                    }}
                >
                    {detail}
                </p>
            ) : null}
            {action}
        </div>
    );
}
